import React, { useContext, useState, useRef, useEffect } from 'react';
import { BaseWidget } from ".";
import { EditorContext } from '../context/EditorContext';
import { BorderControl } from '../controls/grouped/BorderControl';
import { FlexControls } from '../controls/grouped/FlexControls';

import ContainerIcon from '@mui/icons-material/ViewColumnOutlined';
import DeleteIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RowIcon from '@mui/icons-material/ViewWeek';
import ColumnIcon from '@mui/icons-material/ViewAgenda';
import { BackgroundControl } from '../controls/grouped/BackgroundControl';
import { PaddingMarginControl } from '../controls/grouped/PaddingMarginControl';
import { AnimationControl } from '../controls/grouped/AnimationControl';
import useInView from '../../../../hooks/useInView';
import { NumberCssControl } from '../controls/NumberCssControl';
import { buildBackgroundStyles } from '../utils/backgroundStyles';

/**
 * @type {React.FC<{
 *     widget: BaseWidget,
 *     editable: boolean,
 *     paddingMargin: import('../controls/grouped/PaddingMarginControl').PaddingMarginValue,
 *     flex: import('../controls/grouped/FlexControls').FlexControlValue,
 *     background: import('../controls/grouped/BackgroundControl').BackgroundControlValue,
 *     border: import('../controls/grouped/BorderControl').BorderControlValue,
 *     animations: import('../controls/grouped/AnimationControl').AnimationControlValue,
 * }>}
 */
const ContainerRenderer = ({
    widget,
    editable,
    paddingMargin,
    flex,
    background,
    border,
    animations,
    children
}) => {

    const editorCtx = useContext(EditorContext);

    const parent = editorCtx?.getWidgetById(widget.parent) ?? null;
    const isRoot = parent === null;

    /* Animation runs only in Viewer mode (public open page), never in the editor.
       Root container is skipped so the whole report doesn't flash on load. */
    const animationActive = !editable && !isRoot && animations?.type && animations.type !== 'none';
    const [inViewRef, inView] = useInView({ threshold: 0.15, triggerOnce: true });

    /* Editor preview: replay the animation once whenever the user changes
       its type / duration / delay. We use the Web Animations API directly so
       we don't fight with the existing CSS state machine (which is for the
       viewer's scroll-triggered case). Refs hold the last-seen values, so the
       first effect run on mount is a no-op (refs match props). */
    /** @type {React.RefObject<HTMLDivElement>} */
    const containerRef = useRef(null);

    /** @ts-ignore */
    const isEmpty = !children || (children.length === 0);

    /* Hide a floating editing overlay when it would spill outside its
       container (too narrow OR too short) — otherwise it paints over the
       neighbouring container's content. We compare actual bounding boxes, so
       both dimensions are handled and an overlay is only hidden when it really
       overflows. Both overlays stay mounted (so they can always be measured)
       and are hidden via the `controls-hidden` class. */
    /** @type {React.RefObject<HTMLDivElement>} */
    const iconsRef = useRef(null);
    /** @type {React.RefObject<HTMLDivElement>} */
    const quickContentRef = useRef(null);
    const [controlsFit, setControlsFit] = useState(true);
    const [quickFit, setQuickFit] = useState(true);

    useEffect(() => {
        if (!editable) return;
        const el = containerRef.current;
        if (!el) return;

        /* Measure against the OUTER widget box (the size the user actually
           set), not the inner .report-builder-container — empty containers
           force a min-height:120px on the inner, so it balloons past the outer
           and the overlays would otherwise always "fit". The outer is also
           what resizes when the user changes the height, so observe it. */
        const outer = /** @type {HTMLElement} */ (el.closest('.report-builder-widget') ?? el);

        const measure = () => {
            const c = outer.getBoundingClientRect();
            /**
             * @param {HTMLElement | null} node - The overlay to test
             * @param {number} pad - Extra room for decorative pseudo-elements
             *   (the toolbar's ::before/::after triangles aren't in its rect)
             * @returns {boolean} true if the overlay fits inside the container
             */
            const fits = (node, pad = 0) => {
                if (!node) return true;
                const r = node.getBoundingClientRect();
                return r.left - pad >= c.left - 1
                    && r.right + pad <= c.right + 1
                    && r.top >= c.top - 1
                    && r.bottom <= c.bottom + 1;
            };
            /* 12px ≈ the 1em triangle that extends past each side at 0.75rem. */
            setControlsFit(fits(iconsRef.current, 12));
            setQuickFit(fits(quickContentRef.current));
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(outer);
        return () => ro.disconnect();
    }, [editable, isEmpty]);

    const prevTypeRef = useRef(animations?.type);
    const prevDurationRef = useRef(animations?.duration);
    const prevDelayRef = useRef(animations?.delay);
    useEffect(() => {
        if (!editable || isRoot) return;

        const changed = prevTypeRef.current !== animations?.type
            || prevDurationRef.current !== animations?.duration
            || prevDelayRef.current !== animations?.delay;

        prevTypeRef.current = animations?.type;
        prevDurationRef.current = animations?.duration;
        prevDelayRef.current = animations?.delay;

        if (!changed) return;
        if (!animations?.type || animations.type === 'none') return;

        const el = containerRef.current;
        if (!el) return;

        /** @type {Record<string, Keyframe[]>} */
        const KEYFRAMES = {
            'slide-from-top':    [{ opacity: 0, transform: 'translateY(-40px)' }, { opacity: 1, transform: 'translateY(0)' }],
            'slide-from-bottom': [{ opacity: 0, transform: 'translateY(40px)'  }, { opacity: 1, transform: 'translateY(0)' }],
            'fade-in':           [{ opacity: 0 }, { opacity: 1 }],
        };
        const frames = KEYFRAMES[animations.type];
        if (!frames) return;

        const anim = el.animate(frames, {
            duration: animations.duration ?? 600,
            delay: animations.delay ?? 0,
            easing: 'ease-out',
            fill: 'none', // editor: don't leave the container in the "to" frame
        });
        return () => anim.cancel();
    }, [editable, isRoot, animations?.type, animations?.duration, animations?.delay]);

    /** @type {Partial<CSSStyleDeclaration>} */
    const styles = {
        display: 'flex',
        flexDirection: flex.flexDirection || 'column',
        justifyContent: flex.justifyContent || 'flex-start',
        alignItems: flex.alignItems || 'stretch',
        gap: `${flex.gap.value}${flex.gap.unit}`,
        ...buildBackgroundStyles(background),
    };
    
    if (paddingMargin) {
        styles.paddingTop = `${paddingMargin.padding.top}${paddingMargin.padding.unit}`;
        styles.paddingRight = `${paddingMargin.padding.right}${paddingMargin.padding.unit}`;
        styles.paddingBottom = `${paddingMargin.padding.bottom}${paddingMargin.padding.unit}`;
        styles.paddingLeft = `${paddingMargin.padding.left}${paddingMargin.padding.unit}`;

        styles.marginTop = `${paddingMargin.margin.top}${paddingMargin.margin.unit}`;
        styles.marginRight = `${paddingMargin.margin.right}${paddingMargin.margin.unit}`;
        styles.marginBottom = `${paddingMargin.margin.bottom}${paddingMargin.margin.unit}`;
        styles.marginLeft = `${paddingMargin.margin.left}${paddingMargin.margin.unit}`;
    }
    
    if (border) {
        styles.borderColor = border.borderColor || 'transparent';
        styles.borderWidth = border.borderWidth ? `${border.borderWidth}px` : '0';
        styles.borderStyle = border.borderStyle || 'solid';
        styles.borderRadius = border.borderRadius ? `${border.borderRadius}px` : '0';
        
        if (border.borderRadius) {
            styles.overflow = 'hidden'; // Ensure border radius clips children
        }
    }

    if (animationActive) {
        styles['--animation-duration'] = `${animations.duration ?? 600}ms`;
        styles['--animation-delay'] = `${animations.delay ?? 0}ms`;
    }

    const className = [
        'report-builder-container',
        isEmpty ? 'empty' : '',
        animationActive && inView ? 'in-view' : '',
    ].filter(Boolean).join(' ');

    /* Compose viewer's inViewRef and editor's containerRef onto the same div.
       useInView returns a RefObject — assign .current directly. */
    const setRefs = (node) => {
        containerRef.current = node;
        inViewRef.current = node;
    };

    return (
        <div
            ref={setRefs}
            className={className}
            data-animation={animationActive ? animations.type : undefined}
            /** @ts-ignore */
            style={styles}
        >
            {children}

            {/* Not on the outer container: its toolbar lay over the title of
                the first block, so a click on that title hit '+' and silently
                added an empty container (testronde 2026-10-15, B3). Its edit
                button is under Structuur → 'Document', adding a container is
                the 'Container' widget. */}
            {editable && !isRoot && (
                <div
                    className={`report-builder-container-icons${controlsFit ? '' : ' controls-hidden'}`}
                    ref={iconsRef}
                    data-testid="container-toolbar"
                >
                    <EditIcon className="report-builder-container-icon" onClick={() => {
                        editorCtx?.setActiveWidgetId(widget.id);
                    }} />
                    <AddIcon className="report-builder-container-icon" onClick={() => {
                        if (!editorCtx) return;
                        editorCtx.addContainerElements(widget.id, 'sibling');
                    }}/>
                    <ContentCopyIcon
                        className="report-builder-container-icon"
                        titleAccess="Dupliceren"
                        onClick={() => editorCtx?.duplicateWidget(widget.id)}
                    />
                    <DragIndicatorIcon className="report-builder-container-icon drag-handle" />
                    <DeleteIcon className="report-builder-container-icon" onClick={() => {
                        editorCtx?.deleteWidget(widget.id);
                    }} />
                </div>
            )}

            {isEmpty && editable && (
                <div className={`report-builder-container-quickactions${quickFit ? '' : ' controls-hidden'}`}>
                    <div ref={quickContentRef} className="report-builder-container-quickactions-inner">
                        <div className="report-builder-container-quickaction-icon" onClick={() => {
                            if (!editorCtx) return;
                            editorCtx.addContainerElements(widget.id, 'row');
                        }} title="Rij toevoegen">
                            <RowIcon />
                        </div>

                        <div className="report-builder-container-quickaction-icon" onClick={() => {
                            if (!editorCtx) return;
                            editorCtx.addContainerElements(widget.id, 'column');
                        }} title="Kolom toevoegen">
                            <ColumnIcon />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export class Container extends BaseWidget {

    constructor(settings = {}, editable = false) {
        super(settings, editable);

        /** @type {BaseWidget[]} */
        this.children = [];

        /** @type {React.ReactNode[]} */
        this.childNodes = [];
    }

    getName() {
        return 'Container';
    }

    getIcon() {
        return <ContainerIcon />;
    }

    getStyles() {
        // const paddingMargin = this.getSetting('paddingMargin');
        // const background = this.getSetting('background');

        /** @type {Partial<CSSStyleDeclaration>} */
        const styles = {
            width: this.getCssValue('width'), // We set the width/height directly on the CanvasNode here. 
            height: this.getCssValue('height') 
        };

        return styles;
    }

    getControls() {

        // const dir = this.getSetting('flexDirection'); // @todo - naming could be dynamic

        const isRoot = this.parent === null;

        return {
            'paddingMargin': new PaddingMarginControl({
                label: 'Marges',
                description: 'Instellingen voor padding en margin',
                defaultValue: {
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        unit: 'px',
                    },
                    margin: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        unit: 'px',
                    },
                },
                options: {}
            }),
            /* Note: 'width' is used in getStyles(). */
            'width': new NumberCssControl({
                label: 'Breedte',
                description: 'Breedte van de container',
                defaultValue: { value: null, unit: 'auto' },
                options: {
                    min: 10,
                    // max: this.getSetting('width')?.unit === 'px' ? 4000 : 100,
                    max: 10000,
                    step: 1,
                    units: ['px', '%', 'auto']
                }
            }),
            /* Note: 'height' is used in getStyles(). */
            'height': new NumberCssControl({
                label: 'Hoogte',
                description: 'Hoogte van de container',
                defaultValue: { value: null, unit: 'auto' },
                options: {
                    min: 10,
                    // max: this.getSetting('height')?.unit === 'px' ? 4000 : 100,
                    max: 10000,
                    step: 1,
                    units: ['px', '%', 'auto']
                }
            }),
            'flex': new FlexControls({
                label: 'Uitlijning',
                description: 'Flex instellingen voor deze container',
                defaultValue: {
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'stretch',
                    gap: { value: 0, unit: 'px' },
                    basis: { value: 0, unit: 'px' },
                },
                options: {}
            }),
            'background': new BackgroundControl({
                label: 'Achtergrond',
                description: 'Achtergrond instellingen',
                defaultValue: {},
                options: {}
            }),
            'border': new BorderControl({
                label: 'Rand',
                description: 'Stijl van de rand',
                defaultValue: {
                    borderColor: '#000000',
                    borderWidth: 0,
                    borderStyle: 'solid',
                    borderRadius: 0,
                },
                options: {}
            }),
            /* Root container has no scroll-trigger (it's already in view on
               page load) — hide the animation control entirely. */
            ...(isRoot ? {} : {
                'animations': new AnimationControl({
                    label: 'Animaties',
                    description: 'Animatie bij in beeld komen tijdens scrollen',
                    defaultValue: {
                        type: 'none',
                        duration: 600,
                        delay: 0,
                    },
                    options: {}
                }),
            }),
        };
    }

    /**
     * @param {{
     *     [K in keyof ReturnType<typeof this.getControls>]: ReturnType<typeof this.getControls>[K]['defaultValue']
     * }} props
     * @returns {React.ReactNode}
     */
    render(props) {
        return (
            <ContainerRenderer 
                {...props} 
                widget={this}
                editable={this.editable}
            >
                {this.childNodes}
            </ContainerRenderer>
        );
    }
}
